import { useRef, useState } from 'react';
import { Heart, Loader2, X } from 'lucide-react';
import { api } from '../services/api';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AREAS = ['login', 'my resume', 'job cards', 'settings', 'answers', 'website', 'general', "don't know"];

const SENTIMENTS = [
  { value: 'great', label: 'great!' },
  { value: 'idea', label: 'I have an idea' },
  { value: 'rough', label: 'kinda rough' },
  { value: 'broken', label: 'broken/errors' },
];

const FEELING_EMOJIS = ['😃', '🙂', '😑', '🙁', '😡'];
const APP_RATING_EMOJIS = ['💋', '👍', '👌', '👎', '❌'];

const MESSAGE_MAX = 1000;

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [area, setArea] = useState('general');
  const [sentiment, setSentiment] = useState('');
  const [message, setMessage] = useState('');
  const [feeling, setFeeling] = useState('');
  const [appRating, setAppRating] = useState('');
  const [allowFollowup, setAllowFollowup] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEscapeClose(onClose, isOpen);
  useFocusTrap(modalRef, isOpen);

  if (!isOpen) return null;

  const resetAndClose = () => {
    setArea('general');
    setSentiment('');
    setMessage('');
    setFeeling('');
    setAppRating('');
    setAllowFollowup(true);
    setSubmitted(false);
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!sentiment) {
      setError("Please tell us how it's going.");
      return;
    }
    if (!message.trim()) {
      setError('Please tell us how we can help.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.submitAppFeedback({
        area,
        sentiment,
        message: message.trim(),
        feeling: feeling || undefined,
        app_rating: appRating || undefined,
        allow_followup: allowFollowup,
      });
      setSubmitted(true);
    } catch {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Provide Feedback"
    >
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            We <Heart className="w-5 h-5 text-blue-500 fill-blue-500" aria-hidden="true" /> your feedback!
          </h2>
          <button
            onClick={resetAndClose}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {submitted ? (
          <div className="p-6 space-y-4 text-center">
            <p className="text-lg font-semibold text-gray-800">Thanks for the feedback!</p>
            <p className="text-sm text-gray-600">
              Seriously, we do take feedback very seriously. We're a small but mighty team,
              and work hard to support jobseekers like you. Keep it up!
            </p>
            <button
              onClick={resetAndClose}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            <div>
              <label htmlFor="feedback-area" className="block text-sm font-medium text-gray-700 mb-1">
                Area of feedback
              </label>
              <select
                id="feedback-area"
                value={area}
                onChange={(e) => setArea(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {AREAS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>

            <div>
              <span className="block text-sm font-medium text-gray-700 mb-1">How's it going:</span>
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="How's it going">
                {SENTIMENTS.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    role="radio"
                    aria-checked={sentiment === s.value}
                    onClick={() => setSentiment(s.value)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      sentiment === s.value
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="feedback-message" className="block text-sm font-medium text-gray-700 mb-1">
                How can we help?
              </label>
              <textarea
                id="feedback-message"
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
                rows={5}
                maxLength={MESSAGE_MAX}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-y"
                placeholder="Tell us what's on your mind…"
              />
              <div className="text-right text-xs text-gray-500">
                ({message.length}/{MESSAGE_MAX})
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Overall, how are you feeling?</span>
              <div className="flex gap-1" role="radiogroup" aria-label="Overall, how are you feeling?">
                {FEELING_EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    role="radio"
                    aria-checked={feeling === e}
                    onClick={() => setFeeling(feeling === e ? '' : e)}
                    className={`text-xl p-1 rounded-lg transition-transform hover:scale-125 ${
                      feeling === e ? 'bg-primary-100 ring-2 ring-primary-500' : ''
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Overall, how is the App doing?</span>
              <div className="flex gap-1" role="radiogroup" aria-label="Overall, how is the App doing?">
                {APP_RATING_EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    role="radio"
                    aria-checked={appRating === e}
                    onClick={() => setAppRating(appRating === e ? '' : e)}
                    className={`text-xl p-1 rounded-lg transition-transform hover:scale-125 ${
                      appRating === e ? 'bg-primary-100 ring-2 ring-primary-500' : ''
                    }`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={allowFollowup}
                onChange={(e) => setAllowFollowup(e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              Can we email you with follow-up / resolution?
            </label>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-end pt-2 border-t">
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                SUBMIT
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
